import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
  persistent?: boolean;
  action?: {
    label: string;
    handler: () => void;
  };
}

export const useNotificationStore = defineStore('notification', () => {
  // State
  const notifications = ref<Notification[]>([]);

  // Getters
  const hasNotifications = computed(() => notifications.value.length > 0);

  // Actions
  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      id,
      duration: 5000, // Default 5 seconds
      persistent: false,
      ...notification,
    };

    notifications.value.push(newNotification);

    // Auto-remove non-persistent notifications
    if (!newNotification.persistent && newNotification.duration) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearAllNotifications = () => {
    notifications.value = [];
  };

  const showSuccess = (message: string, options?: Partial<Notification>) => {
    return addNotification({
      message,
      type: 'success',
      duration: 4000,
      ...options,
    });
  };

  const showError = (message: string, options?: Partial<Notification>) => {
    return addNotification({
      message,
      type: 'error',
      duration: 6000, // Errors stay longer
      persistent: false,
      ...options,
    });
  };

  const showWarning = (message: string, options?: Partial<Notification>) => {
    return addNotification({
      message,
      type: 'warning',
      duration: 5000,
      ...options,
    });
  };

  const showInfo = (message: string, options?: Partial<Notification>) => {
    return addNotification({
      message,
      type: 'info',
      duration: 4000,
      ...options,
    });
  };

  return {
    // State
    notifications,

    // Getters
    hasNotifications,

    // Actions
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
});
